<template>
  <v-app id="app">
    <ExpiredBar />
    <AppBar v-if="loggedIn" />
    <NotHomeAccountBar />
    <NavigationDrawer v-if="notError && notLogin" />
    <v-main class="greyBody main-frame">
      <v-container class="main-container ma-0 pa-0">
        <router-view v-slot="{ Component, route }" class="align-start">
          <transition>
            <component :is="Component" :key="route.path" />
          </transition>
          <v-overlay
            :model-value="isProOnlyView && openUpsellingCard"
            class="align-center justify-center"
            contained
            persistent
            no-click-animation
          >
            <UpsellingCard @close="openUpsellingCard = false" />
          </v-overlay>
        </router-view>
      </v-container>
    </v-main>
    <InsecurePasswordDialog v-if="showInsecurePasswordDialog" />
    <SSOPreferredDialog
      v-if="showSSOPreferredDialog"
      :showDialog="showSSOPreferredDialog"
      @update:show-dialog="showSSOPreferred = $event"
    />
    <ExpiringTrialDialog
      v-if="showExpiringTrialDialog"
      :show-dialog="showExpiringTrialDialog"
      @close-dialog="preOpenExpiringTrialDialog = false"
    />
    <UnlicensedDialog
      v-if="showUnlisicensedDialog"
      :show-dialog="showUnlisicensedDialog"
      @close-dialog="preOpenUnlicensedDialog = false"
    />
    <SnackbarContainer />
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState, mapWritableState } from 'pinia';
import { authenticationStore } from '@/store/pinia/AuthenticationStore';
import { appStore } from '@/store/pinia/AppStore';
import { posthog } from 'posthog-js';
import { debounce } from 'lodash';
import { licenseStore } from './store/pinia/LicenseStore';
import { onPremises } from './shared/utils';
import { getLoginWithPasswordFromLocalStorage } from './util/authentication';
import { captureOpenExternalLink } from './util/posthog';

// Captured before patching so the wrapper below does not call itself
const originalWindowOpen = window.open.bind(window);

export default defineComponent({
  data() {
    return {
      openUpsellingCard: true,
      preOpenUnlicensedDialog: true,
      preOpenExpiringTrialDialog: true,
      showSSOPreferred: true,
      showInsecureDialog: true,
      comingFromLogin: false
    };
  },

  computed: {
    ...mapState(authenticationStore, [
      'loggedIn',
      'isFreeAccount',
      'loggedInUser',
      'isTrialAccount',
      'isUnlicensedAccount'
    ]),
    ...mapState(licenseStore, ['trialIsSoonExpiring']),
    ...mapState(appStore, ['insecurePassword']),
    ...mapWritableState(appStore, ['windowHeight', 'windowWidth']),
    isProOnlyView() {
      const proOnly = (this.$route.meta.proOnly as boolean) ?? false;
      return proOnly && this.isFreeAccount;
    },
    notError() {
      const routeName = this.$route.name as string;
      return !(routeName?.includes('500') || routeName?.includes('404'));
    },
    notLogin() {
      const routeName = this.$route.name as string;
      return !routeName?.includes('login');
    },
    showInsecurePasswordDialog() {
      return this.insecurePassword && this.loggedIn && !onPremises();
    },
    showSSOPreferredDialog() {
      return (
        this.showSSOPreferred &&
        getLoginWithPasswordFromLocalStorage() &&
        this.loggedIn &&
        this.loggedInUser.ssoRequired &&
        this.loggedInUser.organisation.ssoEnabled
      );
    },
    showUnlisicensedDialog() {
      return (
        this.loggedIn &&
        this.isUnlicensedAccount &&
        this.preOpenUnlicensedDialog &&
        this.comingFromLogin
      );
    },
    showExpiringTrialDialog() {
      return (
        this.loggedIn &&
        this.isTrialAccount &&
        this.trialIsSoonExpiring &&
        this.preOpenExpiringTrialDialog &&
        this.comingFromLogin
      );
    }
  },

  watch: {
    $route(_: any, previous: any) {
      this.openUpsellingCard = true;
      this.comingFromLogin =
        previous.name === 'login' || previous.path === '/auth/oidc-callback';
      posthog.capture('$pageview', {
        $referrer: window.location.origin + previous.fullPath
      });
    }
  },
  created() {
    this.migrateLanguageCode();
    this.handleWindowSizeChange = debounce(
      this.handleWindowSizeChange.bind(this),
      20
    );
    window.addEventListener('resize', this.handleWindowSizeChange.bind());
    document.addEventListener('click', this.handleExternalLinkClick, true);
    this.trackWindowOpen();
  },
  unmounted(): void {
    // this.$destroy();
    this.$.appContext.app.unmount(); // TODO: hack; desstroy doesn't exist anymore
    window.removeEventListener(
      'resize',
      this.handleWindowSizeChange.bind(this)
    );
    document.removeEventListener('click', this.handleExternalLinkClick, true);
    window.open = originalWindowOpen;
  },
  methods: {
    migrateLanguageCode() {
      // Migrate old language codes in localStorage to new format if necessary
      let currentLang = localStorage.getItem('currentLang') || 'de_DE';
      if (currentLang === 'de-DE' || currentLang === 'en-GB') {
        currentLang = currentLang
          .replace('de-DE', 'de_DE')
          .replace('en-GB', 'en_US');
        localStorage.setItem('currentLang', currentLang);
      }
    },
    handleWindowSizeChange() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
    },
    trackWindowOpen() {
      window.open = (
        url?: string | URL,
        target?: string,
        features?: string
      ) => {
        if (url) {
          captureOpenExternalLink(url.toString());
        }
        return originalWindowOpen(url, target, features);
      };
    },
    handleExternalLinkClick(this: void, event: MouseEvent) {
      // Capture phase, so it still reports links whose handlers stop propagation
      const anchor = (event.target as HTMLElement | null)?.closest?.(
        'a[href]'
      ) as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }
      const opensNewTab =
        anchor.target === '_blank' || event.ctrlKey || event.metaKey;
      if (!opensNewTab) {
        return;
      }
      captureOpenExternalLink(anchor.href);
    }
  }
});
</script>

<style lang="scss">
@use '@/styles/colors.scss' as colors;
html {
  overflow-y: hidden !important;
}
.v-menu__content {
  box-shadow: 5px 0 15px -5px colors.$dark-shadow;
}

.main-frame {
  overflow: auto;
}
.main-container {
  position: relative;
  height: 100%;
  min-width: 100%;
}
</style>
